import React, { Component } from "react";
import { PrismDialog, PrismButton, PrismInputText, PrismCheckbox, PrismBanner } from "@prism-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { MonthYearPicker } from "../../MonthYearPicker";
import { formatMonthYear } from "../../../../util/dateUtils";

class CareerForm extends Component {
  state = {
    application_id: this.props.application.id,
    company: "",
    position: "",
    start_date: "",
    end_date: "",
    achievements: "",
    error: false,
    currentlyEmployed: false,
    showDialog: false,
    editIndex: null,
    originalEntry: null,
  };

  handleSave = async () => {
    const entryData = {
      application_id: this.state.application_id,
      company: this.state.company,
      position: this.state.position,
      start_date: this.state.start_date,
      end_date: this.state.currentlyEmployed ? null : this.state.end_date,
      achievements: this.state.achievements,
    };

    if (this.state.editIndex !== null) {
      await this.props.updateCareer(this.state.editIndex, entryData);
    } else {
      await this.props.createCareer(entryData);
    }
    this.clearForm();
  };

  handleEdit = (index) => {
    const entry = this.props.careerEntries[index];
    const snapshot = {
      company: entry.company || "",
      position: entry.position || "",
      start_date: formatMonthYear(entry.start_date),
      end_date: formatMonthYear(entry.end_date),
      currentlyEmployed: !entry.end_date,
    };
    this.setState({
      company: entry.company || "",
      position: entry.position || "",
      start_date: entry.start_date || "",
      end_date: entry.end_date || "",
      achievements: entry.achievements || "",
      currentlyEmployed: !entry.end_date,
      editIndex: index,
      error: false,
      originalEntry: snapshot,
    });
  };

  handleDelete = (index) => {
    this.props.handleDeleteCareer(index);
    if (this.state.editIndex === index) {
      this.clearForm();
    } else if (this.state.editIndex !== null && this.state.editIndex > index) {
      this.setState({ editIndex: this.state.editIndex - 1 });
    }
  };

  clearForm = () => {
    this.setState({
      company: "", position: "", start_date: "", end_date: "",
      achievements: "", error: false, currentlyEmployed: false, editIndex: null,
      originalEntry: null,
    });
  };

  handlePrismInput = (name) => (e) => {
    const value = e.detail?.value ?? e.detail ?? '';
    this.setState({ [name]: value, error: false });
  };

  handleDateChange = (name) => (val) => {
    if (name === "end_date" && val && this.state.start_date && Date.parse(val) < Date.parse(this.state.start_date)) {
      this.setState({ error: true });
    } else {
      this.setState({ [name]: val || '', error: false });
    }
  };

  openDialog = () => {
    this.setState({ showDialog: true });
    this.clearForm();
  };

  closeDialog = () => {
    this.setState({ showDialog: false });
    this.clearForm();
  };

  isFieldDirty = (name) => {
    const { originalEntry } = this.state;
    if (!originalEntry) return false;
    if (name === 'currentlyEmployed') return this.state.currentlyEmployed !== originalEntry.currentlyEmployed;
    if (name === 'start_date' || name === 'end_date') {
      return formatMonthYear(this.state[name]) !== originalEntry[name];
    }
    return this.state[name] !== originalEntry[name];
  };

  getDirtyFields = () => {
    if (!this.state.originalEntry) return [];
    return ['company', 'position', 'start_date', 'end_date', 'currentlyEmployed']
      .filter(f => this.isFieldDirty(f));
  };

  render() {
    const { careerEntries } = this.props;
    const { editIndex, company, position, start_date, end_date, currentlyEmployed, error } = this.state;
    const isEditing = editIndex !== null;
    const dirtyFields = this.getDirtyFields();
    const hasDirtyFields = dirtyFields.length > 0;

    // For "currently employed" checkbox: allow it if editing an entry that already has no end_date, or if no other entry is current
    const alreadyHasCurrent = careerEntries.some((e, i) => !e.end_date && i !== editIndex);

    const canSave =
      company.trim().length > 0 &&
      position.trim().length > 0 &&
      start_date.length > 0 &&
      (currentlyEmployed || end_date) &&
      !error;

    return (
      <div>
        <PrismButton tone="primary" display="outline" onPrismClick={this.openDialog}>
          <FontAwesomeIcon icon={faPencil} style={{ marginRight: '0.4rem' }} />
          Manage Career Entries
        </PrismButton>

        <PrismDialog
          open={this.state.showDialog}
          setAriaLabel="Manage Career Entries"
          close-method="user"
          onPrismClose={this.closeDialog}
        >
          <span slot="header">Manage Career Entries</span>

          {/* Entry list */}
          <div style={{ marginBottom: '1.5rem' }}>
            {careerEntries.length === 0 ? (
              <p style={{ color: 'var(--color-text-faded)', fontStyle: 'italic' }}>
                No career entries yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[...careerEntries]
                  .map((entry, idx) => ({ ...entry, _idx: idx }))
                  .sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0))
                  .map((entry) => (
                  <div
                    key={entry.id || entry._idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '4px',
                      backgroundColor: editIndex === entry._idx ? 'var(--color-surface-alt)' : 'var(--color-surface)',
                      border: editIndex === entry._idx ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                      boxShadow: editIndex === entry._idx ? '0 0 0 1px var(--color-primary)' : 'none',
                      transition: 'border 0.15s, box-shadow 0.15s, background-color 0.15s',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{entry.company} — {entry.position}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)' }}>
                        {formatMonthYear(entry.start_date)} – {entry.end_date ? formatMonthYear(entry.end_date) : 'Present'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => this.handleEdit(entry._idx)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-primary)', fontSize: '0.9rem', padding: '0.25rem 0.4rem',
                        }}
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => this.handleDelete(entry._idx)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-danger)', fontSize: '0.9rem', padding: '0.25rem 0.4rem',
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add/Edit form */}
          <div style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: '1rem',
          }}>
            <h6 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
              {isEditing ? 'Edit Entry' : 'Add New Entry'}
            </h6>
            {isEditing && hasDirtyFields && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.8rem', color: 'var(--color-warning)',
                marginBottom: '0.75rem',
              }}>
                <span style={{
                  display: 'inline-block', width: '6px', height: '6px',
                  borderRadius: '50%', backgroundColor: 'var(--color-warning)',
                }} />
                {dirtyFields.length} unsaved {dirtyFields.length === 1 ? 'change' : 'changes'}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{
                borderLeft: isEditing && this.isFieldDirty('company') ? '3px solid var(--color-warning)' : '3px solid transparent',
                paddingLeft: '0.5rem', transition: 'border-color 0.15s',
              }}>
                <PrismInputText
                  label="Company"
                  name="company"
                  value={company}
                  required
                  onPrismInput={this.handlePrismInput('company')}
                />
              </div>
              <div style={{
                borderLeft: isEditing && this.isFieldDirty('position') ? '3px solid var(--color-warning)' : '3px solid transparent',
                paddingLeft: '0.5rem', transition: 'border-color 0.15s',
              }}>
                <PrismInputText
                  label="Position"
                  name="position"
                  value={position}
                  required
                  onPrismInput={this.handlePrismInput('position')}
                />
              </div>

              <div style={{
                borderLeft: isEditing && this.isFieldDirty('start_date') ? '3px solid var(--color-warning)' : '3px solid transparent',
                paddingLeft: '0.5rem', transition: 'border-color 0.15s',
              }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>Start Date</label>
                <MonthYearPicker
                  value={start_date || null}
                  onChange={this.handleDateChange('start_date')}
                  required
                />
              </div>

              <div style={{
                borderLeft: isEditing && (this.isFieldDirty('end_date') || this.isFieldDirty('currentlyEmployed')) ? '3px solid var(--color-warning)' : '3px solid transparent',
                paddingLeft: '0.5rem', transition: 'border-color 0.15s',
              }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>End Date</label>
                <MonthYearPicker
                  value={currentlyEmployed ? null : (end_date || null)}
                  onChange={this.handleDateChange('end_date')}
                  disabled={currentlyEmployed}
                />
                <div style={{ marginTop: '0.5rem' }}>
                  <PrismCheckbox
                    label="I currently work here"
                    checked={currentlyEmployed}
                    disabled={alreadyHasCurrent && !currentlyEmployed}
                    onPrismChange={(e) => {
                      // Prism v9 echoes prismChange when `checked` is set from React; ignore the echo.
                      const next = typeof e?.detail?.checked === 'boolean' ? e.detail.checked : !currentlyEmployed;
                      if (next === this.state.currentlyEmployed) return;
                      this.setState({ currentlyEmployed: next, end_date: '', error: false });
                    }}
                  />
                </div>
              </div>

              <div style={{ display: error ? undefined : 'none' }}>
                <PrismBanner tone="danger" layout="inline">End date must be after start date.</PrismBanner>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  style={{
                    backgroundColor: canSave ? 'var(--color-primary)' : 'var(--color-disabled)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.4rem 1rem',
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    fontSize: '0.85rem',
                    opacity: canSave ? 1 : 0.6,
                  }}
                  disabled={!canSave}
                  onClick={this.handleSave}
                >
                  {isEditing ? 'Update' : 'Add'}
                </button>
                <span style={{ display: isEditing ? undefined : 'none' }}>
                  <button
                    type="button"
                    onClick={this.clearForm}
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--color-text)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      padding: '0.4rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    Cancel
                  </button>
                </span>
              </div>
            </div>
          </div>

          <div slot="footer">
            <PrismButton display="outline" onPrismClick={this.closeDialog}>
              Done
            </PrismButton>
          </div>
        </PrismDialog>
      </div>
    );
  }
}

export default CareerForm;
